import { Analytics } from '@cobliteam/analytics';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import { useTranslation } from '@cobliteam/i18n';
import Tag from '@cobliteam/ui/components/Tag';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { cleanCPF, isValidCPF } from '@cobliteam/ui-business/utils/cpfValidator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import { rowsForRetry, runSequentialSaves } from './sequentialSubmit';

/** Rows shown in the modal (Figma "Cadastre motoristas"); only the first is required. */
const ROWS = 5;

/** Minimal "register drivers" modal — up to five rows of name + optional CPF. */
const DriversForm = ({ fleetId, item, onClose, onSuccess, onAdvancedSettings }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { useSaveDriver, getDrivers } = useDriver();
  const { mutateAsync: saveDriver, isPending } = useSaveDriver();
  const driversQuery = getDrivers({});

  const schema = useMemo(() => {
    const existingNames = new Set((driversQuery.data ?? []).map((driver) => driver.name.trim().toLowerCase()));
    const existingCPFs = new Set((driversQuery.data ?? []).map((driver) => driver.cpf).filter(Boolean));
    return z.object({
      rows: z.array(z.object({ name: z.string(), cpf: z.string() })).superRefine((rows, ctx) => {
        rows.forEach((row, index) => {
          const name = row.name.trim();
          const cpf = row.cpf.trim();
          if (!(name || cpf)) {
            // Only the first row is mandatory; later rows may stay empty.
            if (index === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'name'], message: t('entity_modal.drivers.validation.name_required') });
            return;
          }
          if (name) {
            // Same duplicate rule as the DriverDrawer schema: exact name already on the fleet.
            const repeatedOnFleet = existingNames.has(name.toLowerCase());
            const repeatedOnRows = rows.some((other, otherIndex) => otherIndex < index && other.name.trim().toLowerCase() === name.toLowerCase());
            if (repeatedOnFleet || repeatedOnRows) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'name'], message: t('entity_modal.drivers.validation.name_duplicated') });
            }
          } else {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'name'], message: t('entity_modal.drivers.validation.name_required') });
          }
          if (cpf) {
            if (isValidCPF(cpf)) {
              const cleaned = cleanCPF(cpf);
              const repeatedOnFleet = existingCPFs.has(cleaned);
              const repeatedOnRows = rows.some((other, otherIndex) => otherIndex < index && cleanCPF(other.cpf) === cleaned);
              if (repeatedOnFleet || repeatedOnRows) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'cpf'], message: t('entity_modal.drivers.validation.cpf_duplicated') });
              }
            } else {
              ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'cpf'], message: t('entity_modal.drivers.validation.cpf_invalid') });
            }
          }
        });
      }),
    });
  }, [driversQuery.data, t]);

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { rows: Array.from({ length: ROWS }, () => ({ name: '', cpf: '' })) },
  });

  const handleSubmit = async ({ rows }: FormValues) => {
    // Original indexes travel along so a partial failure can clear exactly the rows that saved.
    const filled = rows.map((row, rowIndex) => ({ row, rowIndex })).filter(({ row }) => row.name.trim());
    // Sequential saves; a failed row doesn't stop the following ones and the hook toasts each failure.
    const { succeeded, failed } = await runSequentialSaves(filled, ({ row }) => {
      const cpf = cleanCPF(row.cpf);
      return saveDriver({ id: '', fleetId: fleetId ?? '', name: row.name.trim(), phoneNumbers: [], ...(cpf ? { cpf } : {}) });
    });
    // Created drivers exist even when another row fails, so the event reflects reality.
    if (succeeded.length > 0) Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'drivers', count: succeeded.length });
    if (failed.length > 0) {
      // Keep the form open for retry, dropping the rows that saved so retrying can't duplicate them.
      form.reset({ rows: rowsForRetry(rows, new Set(succeeded.map(({ rowIndex }) => rowIndex)), ROWS, () => ({ name: '', cpf: '' })) });
      void form.trigger();
      return;
    }
    onSuccess(succeeded.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.drivers.title')}
      description={t('entity_modal.drivers.description')}
      secondaryAction={{ label: t('entity_modal.advanced_settings'), onClick: onAdvancedSettings }}
      primaryAction={{
        label: t('entity_modal.drivers.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !form.formState.isValid,
        loading: isPending,
        endIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-drivers-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.drivers.progress', { current: item.current, target: item.target })} />
          {Array.from({ length: ROWS }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size row list, never reordered
            <SetupGuideFormRow key={index} label={t('entity_modal.drivers.row_label', { index: index + 1 })}>
              <FormField control={form.control} name={`rows.${index}.name`}>
                {({ field }) => (
                  <FormItem className='w-72'>
                    <FormLabel>{t('entity_modal.drivers.name_label')}</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete='off' data-1p-ignore placeholder={t('entity_modal.drivers.name_placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              </FormField>
              <FormField control={form.control} name={`rows.${index}.cpf`}>
                {({ field }) => (
                  <FormItem className='w-56'>
                    <FormLabel>{t('entity_modal.drivers.cpf_label')}</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode='numeric' placeholder={t('entity_modal.drivers.cpf_placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              </FormField>
            </SetupGuideFormRow>
          ))}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default DriversForm;
